import { RawDraftContentState } from "draft-js";
import { useArticleDetailsQuery } from "entities/manageArticles/queries/useArticleDetailsQuery";
import { invalidateArticleGroupListQuery } from "entities/manageArticles/queries/useArticleGroupListQuery";
import { useUpdateArticleQuery } from "entities/manageArticles/queries/useUpdateArticleQuery";
import {
  ArticleBrandId,
  ArticleCategory,
  ArticleGroupIds,
  ArticleStatus,
  ArticleToUpdate,
} from "entities/manageArticles/types";
import isEqual from "lodash/isEqual";
import React, {
  FC,
  PropsWithChildren,
  useEffect,
  useState,
  useRef,
} from "react";
import { useParams } from "react-router-dom";
import { LocaleCode } from "shared/types/languages";
import { createContext } from "shared/utils/context";
import { convertDraftToMarkdown } from "shared/utils/convertDraftToMarkdown";
import { ArticleProps } from "./types";

const [Provider, useArticlePage] = createContext<ArticleProps>({
  name: "ArticlePageContext",
  hookName: "useArticlePage",
  providerName: "ArticlePageProvider",
});

const ArticlePageProvider: FC<PropsWithChildren> = ({ children }) => {
  const [editorContent, setEditorContent] = useState<
    RawDraftContentState | undefined
  >();
  const [version, setVersion] = useState<string>("");
  const [markdownContent, setMarkdownContent] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [open, setOpen] = useState(false);
  const params = useParams();
  const brandId = params.brandId as ArticleBrandId;
  const categoryId = params.categoryId as ArticleCategory;
  const groupId = params.groupId as ArticleGroupIds;
  const articleId = params.id as string;

  const {
    data: articleDetails,
    isLoading: isLoadingArticle,
    isSuccess,
  } = useArticleDetailsQuery({ brandId, categoryId, groupId, articleId });

  const { mutateAsync: updateArticle, isPending: isSubmittingArticle } =
    useUpdateArticleQuery();

  const hasInitialized = useRef(false);

  useEffect(() => {
    if (articleDetails && isSuccess && !hasInitialized.current) {
      setVersion(articleDetails.version);
      if (articleDetails.content?.editorContent) {
        setEditorContent(articleDetails.content.editorContent);
        setMarkdownContent(articleDetails.content.text);
        setHasChanges(false);
        hasInitialized.current = true;
      }
    }
  }, [articleDetails, isSuccess]);

  useEffect(() => {
    const hasEditorEmptyBlock = editorContent?.blocks.every(
      (block) => block.text === "",
    );

    if (
      !isEqual(articleDetails?.content?.editorContent, editorContent) &&
      !hasEditorEmptyBlock
    ) {
      setHasChanges(true);
    } else {
      setHasChanges(false);
    }
  }, [articleDetails?.content?.editorContent, editorContent]);

  const onResetChanges = () => {
    setOpen(false);
    setHasChanges(false);
    invalidateArticleGroupListQuery({ brandId, categoryId, groupId });
  };

  const onSaveArticleDetails = async () => {
    const article = {
      content: {
        text: markdownContent ?? "",
        editorContent,
      },
      version,
      updatedAt: new Date(),
      status: ArticleStatus.PUBLISHED,
      language: articleDetails?.language as LocaleCode,
    } as ArticleToUpdate;

    await updateArticle(
      {
        brandId,
        categoryId,
        groupId,
        articleId,
        article,
      },
      {
        onSuccess: onResetChanges,
      },
    );
  };

  const handleEditorChange = (content: RawDraftContentState) => {
    setEditorContent(content);
    const markdown = convertDraftToMarkdown(content);
    setMarkdownContent(markdown);

    const isChangedContent = !isEqual(
      content,
      articleDetails?.content?.editorContent,
    );

    setHasChanges(isChangedContent);
  };

  const handleSaveDraft = async () => {
    const article: ArticleToUpdate = {
      content: {
        text: markdownContent ?? "",
        editorContent: editorContent ?? null,
      },
      version,
      updatedAt: new Date(),
      status: ArticleStatus.UNPUBLISHED,
      language: articleDetails?.language as LocaleCode,
    };

    await updateArticle(
      {
        brandId,
        categoryId,
        groupId,
        articleId,
        article,
      },
      {
        onSuccess: onResetChanges,
      },
    );
  };

  const isDisabledSubmission =
    !version ||
    (version === articleDetails?.version && !hasChanges) ||
    !markdownContent;

  const value = {
    articleDetails,
    isLoadingArticle,
    version,
    setVersion,
    onSaveArticleDetails: async () => await onSaveArticleDetails(),
    editorContent,
    handleEditorChange,
    isDisabledSubmission,
    isSubmittingArticle,
    hasChanges,
    setHasChanges,
    open,
    setOpen,
    handleSaveDraft,
  } as ArticleProps;

  return <Provider value={value}>{children}</Provider>;
};

export { ArticlePageProvider, useArticlePage };
